import { Box, Button, Card, CardContent, CardHeader, Grid, Menu, MenuItem, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { config, styles } from "../../style";
import { Calendar } from "../../icons/calendar";
import { UserIcon } from "../../icons/user-icon";
import { Location } from "../../icons/location";
import SortIcon from "@mui/icons-material/Sort";
import { proposalsSkeleton } from "./skeleton";
import { useAuth } from "../../hooks/useAuth";
import { query } from "../../services/salesforce";
import { format } from "date-fns";
import { EventCard } from "../../components/EventCard";
import { useNavigate } from "react-router-dom";
import NoDataPresentUI from "../../components/NoDataPresentUI";
import { useDispatch, useSelector } from "react-redux";
import { initialize, selectDashboardData } from "./dashboardSlice";

// const constantDataList = [
//     {
//         eventName: "2024 Financial Roundtable NYC",
//         startPlayDate: "April 15th - April 16th 2024",
//         location: "New York, NY",
//         ownerName: "Nancy Appleton",
//     },
//     {
//         eventName: "2024 Health and Wellness Conference",
//         startPlayDate: "April 15th - April 16th 2024",
//         location: "New York, NY",
//         ownerName: "Nancy Appleton",
//     },
// ];

export const formatTimeWithZone = (dateToFormat) => {
    if (dateToFormat) {
        // console.log('TIME ZONE')
        // console.log(dateToFormat)
        let num = new Date().getTimezoneOffset();

        if (num < 0) {
            num = Math.abs(num);
            const hours = String(Math.floor(num / 60)).padStart(2, "0");
            const minutes = String(Math.floor(num % 60)).padStart(2, "0");
            let timeZone = `${hours}:${minutes}`;
            return format(new Date(dateToFormat + "T05:00:00+" + timeZone), "MMMM d, yyyy");
        }
        const hours = String(Math.floor(num / 60)).padStart(2, "0");
        const minutes = String(Math.floor(num % 60)).padStart(2, "0");
        let timeZone = `${hours}:${minutes}`;
        // console.log(dateToFormat + 'T05:00:00-' + timeZone)
        return format(new Date(dateToFormat + "T05:00:00-" + timeZone), "MMMM d, yyyy");
    }
    return "";
};
function Proposals() {
    const dashboardData = useSelector(selectDashboardData)
    const [anchorSortEl, setAnchorSortEl] = React.useState(null);
    const sortOpen = Boolean(anchorSortEl);
    const [proposalSortBy, setProposalSortBy] = useState("Newest proposals");

    const [data, setData] = useState(dashboardData.proposals||null);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState(''); // 'idle' | 'loading' | 'succeeded' | 'failed'
    const dispatch = useDispatch();

    const navigate = useNavigate();
    const { getUserDetails, getUserDetailsNotAsync } = useAuth();
    const userDetails = getUserDetailsNotAsync();
    const handleSortMenuClick = (event) => {
        setAnchorSortEl(event.currentTarget);
    };

    const handleSortMenuClose = (sortBy) => {
        if (!sortBy) {
            setAnchorSortEl(null);
            return;
        }
        setAnchorSortEl(null);
        setProposalSortBy(sortBy);
    };

    const getSharedMessageText = (first, totalLength) => {
        if (totalLength === 0) {
            return `This has been shared with ${first}.`;
        }
        if (totalLength === 1) {
            return `This has been shared with ${first} and one other person.`;
        }

        return `This has been shared with ${first} and ${totalLength} other people.`;
    };

    const getSharedMessage = (record) => {
        let opportunityContactRoles = record.opportunityContactRoles?.filter(
            (role) => role.Contact.Id !== userDetails.contact.id
        );
        let opportunityContactRolesShared = [
            ...(opportunityContactRoles?.map((item) => {
                return {
                    id: item.id,
                    contactId: item.Contact?.Id,
                    name: item.Contact?.FirstName + " " + item.Contact?.LastName,
                    email: item.Contact?.Email,
                };
            }) || []),
            ...(record.proposalParticipants?.map((item) => {
                return {
                    id: item.id,
                    contactId: item.Contact__r?.Id,
                    name: item.Contact__r?.FirstName + " " + item.Contact__r?.LastName,
                    email: item.Email__c,
                };
            }) || []),
        ];
        if (opportunityContactRolesShared?.length > 0) {
            let withNames = opportunityContactRolesShared.find((role) => role.contactId);
            if (withNames) {
                return getSharedMessageText(withNames.name, opportunityContactRolesShared.length - 1);
            } else {
                let withEmail = opportunityContactRolesShared.find((role) => role.email);
                if (withEmail) {
                    return getSharedMessageText(withEmail.email, opportunityContactRolesShared.length - 1);
                }
            }
        }

        return "This has been shared with some people.";
    };

    const getRecords = () => {
        if (data?.records?.length > 0) {
            let recordsToSort = [
                ...data?.records?.map((item) => {
                    return {
                        ...item,
                        sharedText: getSharedMessage(item),
                    };
                }),
            ];

            if (proposalSortBy === "Newest proposals") {
                recordsToSort = recordsToSort.sort((a, b) =>
                    new Date(a.createdDate) > new Date(b.createdDate)
                        ? -1
                        : new Date(b.createdDate) > new Date(a.createdDate)
                        ? 1
                        : 0
                );
            } else if (proposalSortBy === "Event date") {
                recordsToSort = recordsToSort.sort((a, b) =>
                    new Date(a.startPlayDate) > new Date(b.startPlayDate)
                        ? 1
                        : new Date(b.startPlayDate) > new Date(a.startPlayDate)
                        ? -1
                        : 0
                );
            } else if (proposalSortBy === "Last updated") {
                recordsToSort = recordsToSort.sort((a, b) =>
                    new Date(a.lastUpdatedDate) > new Date(b.lastUpdatedDate)
                        ? -1
                        : new Date(b.lastUpdatedDate) > new Date(a.lastUpdatedDate)
                        ? 1
                        : 0
                );
            } else if (proposalSortBy === "Created date") {
                recordsToSort = recordsToSort.sort((a, b) =>
                    new Date(a.createdDate) > new Date(b.createdDate)
                        ? -1
                        : new Date(b.createdDate) > new Date(a.createdDate)
                        ? 1
                        : 0
                );
            }

            return (
                <Grid container spacing={3} alignItems="stretch">
                    {recordsToSort.map((record) => (
                        <Grid
                            item
                            key={record.id}
                            xs={12}
                            sm={12}
                            md={12}
                            sx={{
                                "@media (min-width: 1200px) and (max-width:1300px)": {
                                    width: "50%",
                                },
                                "@media (min-width: 600px) and (max-width:650px)": {
                                    width: "100%",
                                },
                            }}>
                            <EventCard
                                event={record}
                                type="proposal"
                                displayProposalLink={record.hasAccess}
                                onProposalLinkClick={() => handleCardClick(record.id)}></EventCard>
                        </Grid>
                    ))}
                </Grid>
            );
        }
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "310px" }}>
                No proposal found
            </Box>
        );
    };

    useEffect(() => {
        const getAllProposals = async () => {
            setStatus("loading");
            const userDetails = await getUserDetails();
            try {
                const result = await query("opportunity", "getOpenParentOpportunityForDashboard", {
                    accountId: userDetails?.contact?.accountId,
                    contactId: userDetails?.contact?.id,
                    queryFields: "getQueryFieldsList",
                });
                if (result.isSuccess) {
                    const updatedData = {
                        records: result.data.map((item) => ({
                            ...item,
                            hasAccess: !!item.opportunityContactRoles?.find(
                                (i) => i.Contact.Id === userDetails?.contact?.id
                            ),
                        })),
                        userDetails: userDetails,
                    }
                    setData(updatedData);
                    setStatus("succeeded");
                    dispatch(
                        initialize({
                            proposals: updatedData,
                        })
                    );
                } else {
                    console.log("result.errorMessage", result.errorMessage);
                    setError(result.errorMessage);
                    setStatus("failed");
                    setData({ records: [], userDetails: userDetails });
                }
            } catch (error) {
                setStatus("failed");
                setData({ records: [], userDetails: userDetails });
                if (error instanceof Error) {
                    setError(error.message); // This gives the actual error message
                } else {
                    setError("Something went wrong"); // Fallback in case it's not an Error object
                }
            }
        };

        if (dashboardData.proposals) {
            setData(dashboardData.proposals);
            // setStatus("succeeded");
        } else {
            getAllProposals();
        }
    }, []);

    const handleCardClick = (recordId) => {
        // navigate("/proposal/" + recordId);
        window.open("/proposal/" + recordId, "_blank");
    };

    return (
        <div>
            <Paper
                sx={{
                    borderRadius: "10px",
                    padding: "1rem",
                    boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)",
                }}
            >
                <Typography
                    component={"h1"}
                    sx={{
                        ...styles.paperHeader,
                    }}
                >
                    Open Proposals
                    <Typography component={"span"} sx={{ ...styles.paperTag, marginLeft: "4px" }}>
                        ({data?.records?.length})
                    </Typography>
                </Typography>

                <Box>
                    <Button
                        id="sort-proposals"
                        aria-controls={sortOpen ? "proposals-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={sortOpen ? "true" : undefined}
                        onClick={handleSortMenuClick}
                        startIcon={<SortIcon fontSize="small" sx={{ color: config.primaryColor }} />}
                        sx={{
                            borderRadius: "4px",
                            border: "1px solid #E9E9E9",
                            background: "#F9FAFC",
                            padding: "2px 8px",
                            marginTop: "1rem",
                        }}
                        variant="outlined"
                    >
                        {`Sort by: ${proposalSortBy}`}
                    </Button>
                    <Menu
                        id="proposals-menu"
                        anchorEl={anchorSortEl}
                        open={sortOpen}
                        onClose={() => handleSortMenuClose("")}
                        MenuListProps={{
                            "aria-labelledby": "sort-proposals",
                        }}
                    >
                        <MenuItem onClick={() => handleSortMenuClose("Newest proposals")} value="Newest proposals">
                            Newest proposals
                        </MenuItem>
                        <MenuItem onClick={() => handleSortMenuClose("Last updated")} value="Last updated">
                            Last updated
                        </MenuItem>
                        <MenuItem onClick={() => handleSortMenuClose("Created date")} value="Created date">
                            Created date
                        </MenuItem>
                        <MenuItem onClick={() => handleSortMenuClose("Event date")} value="Event date">
                            Event date
                        </MenuItem>
                    </Menu>
                </Box>

                {status === "loading" ? (
                    proposalsSkeleton
                ) : (
                    <Box
                        marginTop={"1rem"}
                        overflow={"auto"}
                        minHeight={"310px"}
                        maxHeight={"310px"}
                        sx={{
                            overflowY: "scroll",
                            scrollbarWidth: "none", // Firefox
                            msOverflowStyle: "none", // IE/Edge
                            "&::-webkit-scrollbar": {
                                display: "none", // Chrome/Safari
                            },
                        }}
                    >
                        {getRecords()}
                    </Box>
                )}
            </Paper>
        </div>
    );
}

export default Proposals;
